import { useState } from 'react';

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import { useFetchOrgActiveRuns } from '@/api/queries/analyticsQueries/useFetchOrgRuns';
import useGetOrganisation from '@/api/queries/useGetOrganisation';
import { useGlobalAdminCheck } from '@/hooks/useGlobalAdminCheck';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { SearchInput } from '@/new/components/search-input';
import { TableHeader, TableRow, TableHead, TableBody, TableCell, Table } from '@/new/components/shadcn/table';
import { CellWithCopy } from '@/new/components/table-cell-with-copy';
import { TableFooter } from '@/new/components/table-footer';
import { Run } from '@/types';

const fallbackTableData: Run[] = [];

const columnHelper = createColumnHelper<Run>();
const columns = [
  columnHelper.accessor('id', {
    header: () => <span>Run Id</span>,
    cell: (info) => (
      <CellWithCopy textToCopy={info.getValue()}>
        <span className="line-clamp-1 max-w-42 break-all">{info.getValue()}</span>
      </CellWithCopy>
    ),
  }),
  columnHelper.accessor('caseId', {
    header: () => <span>Case Id</span>,
    cell: (info) => (
      <CellWithCopy textToCopy={info.getValue()}>
        <span className="line-clamp-1 max-w-42 break-all">{info.getValue()}</span>
      </CellWithCopy>
    ),
  }),
  columnHelper.accessor('status', {
    header: () => <span>Status</span>,
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  columnHelper.accessor('createdBy.email', {
    header: () => <span>Created By</span>,
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  columnHelper.accessor('createdDate', {
    header: () => <span>Created Date</span>,
    cell: (info) => (
      <span className="whitespace-nowrap">
        {new Date(info.getValue()).toLocaleString('en-GB', {
          dateStyle: 'medium',
          timeStyle: 'medium',
        })}
      </span>
    ),
  }),
];

export function OrganisationRuns() {
  const { organisationId } = useParams();
  if (!organisationId) return null;

  useGlobalAdminCheck();

  const { data: organisation } = useGetOrganisation(organisationId);
  const { data: runsData, isLoading: isLoadingRuns } = useFetchOrgActiveRuns(organisationId);

  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data: runsData ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [
        {
          id: 'createdDate',
          desc: true,
        },
      ],
      pagination: {
        pageSize: 10,
      },
    },
    state: {
      globalFilter,
    },
  });

  return (
    <>
      <Breadcrumb
        items={
          organisation
            ? [
                { href: '/admin/organisations', label: 'Admin', active: false },
                {
                  href: `/admin/organisations/${organisation.id}`,
                  label: organisation.name,
                  active: false,
                },
                {
                  href: `/admin/organisations/${organisation.id}/runs`,
                  label: 'Runs',
                  active: true,
                },
              ]
            : [{ href: '/admin/organisations', label: 'Admin', active: false }]
        }
      />

      <PageContainer>
        <PageHeader>
          <PageTitle text="Runs" />
        </PageHeader>

        <SearchInput
          placeholder="Search runs..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          className="my-6 max-w-sm"
        />

        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Pagination Controls */}
        {!isLoadingRuns && <TableFooter table={table} plural="runs" />}
      </PageContainer>
    </>
  );
}
